import { Button } from '@hyperflake/react-ui-library';
import { Point, Polygon, Tool } from 'modules/site-walk-gui/helper/types';
import { ZONE_COLORS } from 'modules/site-walk-gui/helper/constants';
import useFloorPlanListData from 'modules/site-walk/hooks/useFloorPlanListData';
import { useParams } from 'react-router-dom';
import { Popover } from '@library/popover';

interface ToolbarProps {
    isEditMode: boolean;
    activeTool: Tool;
    inProgressPoints: Point[];
    selectedPolygon: Polygon | undefined;
    polygonCount: number;
    isSaving: boolean;
    canSave: boolean;
    saveError: string | null;
    openDeletePopover: boolean;
    setOpenDeletePopover: (open: boolean) => void;
    onEnterEdit: () => void;
    onSave: () => void;
    onUpload: () => void;
    onCancelEdit: () => void;
    onDeleteSelected: () => void;
    onClearAll: () => void;
    onChangePolygonColor: (color: string) => void;
    onChangePolygonFloorPlan: (floorPlan: string) => void;
}

const StatusHints = ({
    isEditMode,
    activeTool,
    inProgressPoints,
    selectedPolygon,
    canSave,
    saveError,
}: Pick<
    ToolbarProps,
    'isEditMode' | 'activeTool' | 'inProgressPoints' | 'selectedPolygon' | 'canSave' | 'saveError'
>) => (
    <span className="flex items-center gap-2 min-w-0">
        {saveError && (
            <span className="text-red-400 flex items-center gap-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
                    <path d="M6 3.5v3M6 8.5v.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                {saveError}
            </span>
        )}
        {isEditMode && inProgressPoints.length > 0 && (
            <span className="text-blue-400 truncate">
                {inProgressPoints.length} points — click first point or dbl-click to close
            </span>
        )}
        {isEditMode && activeTool === 'select' && selectedPolygon && inProgressPoints.length === 0 && (
            <span className="text-amber-400 truncate">
                Drag vertices · Click edge midpoints to add · Double-click vertex to remove
            </span>
        )}
        {isEditMode && !canSave && inProgressPoints.length === 0 && (
            <span className="text-slate-500 truncate">
                Select each polygon and fill in its Floor Plan name to enable save
            </span>
        )}
    </span>
);

const SaveButton = ({ isSaving, canSave, onSave }: { isSaving: boolean; canSave: boolean; onSave: () => void }) => {
    const isDisabled = isSaving || !canSave;
    const title = !canSave ? 'Fill in the Floor Plan name for each polygon before saving' : undefined;

    return (
        <Button
            onClick={onSave}
            size="xs"
            disabled={isDisabled}
            title={title}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-all text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
            {isSaving ? (
                <>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="animate-spin">
                        <circle
                            cx="6"
                            cy="6"
                            r="4.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeDasharray="14 8"
                            strokeLinecap="round"
                        />
                    </svg>
                    Saving…
                </>
            ) : (
                <>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    Save
                </>
            )}
        </Button>
    );
};

// const InlineProperties = ({
//     polygon,
//     onChangeColor,
//     onChangeFloorPlan,
// }: {
//     polygon: Polygon;
//     onChangeColor: (color: string) => void;
//     onChangeFloorPlan: (floorPlan: string) => void;
// }) => {
//     const { clientId, projectId } = useParams();
//     const { floorPlans, isLoading } = useFloorPlanListData({ clientId, projectId });

//     const floorPlanMissing = polygon.floorPlan.trim() === '';

//     return (
//         <div className="flex items-center gap-4 pl-4 border-l border-white/10 shrink-0">
//             <div className="flex flex-col gap-1 min-w-[220px]">
//                 <label className="text-[10px] text-slate-500 flex items-center gap-1 uppercase tracking-wider">
//                     Floor Plan
//                     {floorPlanMissing && <span className="text-amber-400 normal-case">required</span>}
//                 </label>
//                 <select
//                     value={polygon.floorPlan}
//                     disabled={isLoading}
//                     onChange={(e) => onChangeFloorPlan(e.target.value)}
//                     className={`bg-[#0f172a] text-white text-xs px-2 py-1.5 rounded border outline-none transition-colors cursor-pointer disabled:opacity-50 ${
//                         floorPlanMissing
//                             ? 'border-amber-500/60 focus:border-amber-400'
//                             : 'border-white/10 focus:border-blue-500'
//                     }`}
//                 >
//                     <option value="" disabled>
//                         {isLoading ? 'Loading…' : 'Select floor plan…'}
//                     </option>
//                     {floorPlans?.map((fp) => (
//                         <option key={fp._id} value={fp._id}>
//                             {fp.name}
//                         </option>
//                     ))}
//                 </select>
//             </div>

//             <div className="flex flex-col gap-1">
//                 <label className="text-[10px] text-slate-500 uppercase tracking-wider">Color</label>
//                 <div className="flex  gap-1.5 max-w-[120px]">
//                     {ZONE_COLORS.map((color) => (
//                         <button
//                             key={color}
//                             type="button"
//                             onClick={() => onChangeColor(color)}
//                             className="w-5 h-5 rounded transition-transform hover:scale-110"
//                             style={{
//                                 backgroundColor: color,
//                                 outline: polygon.color === color ? '2px solid white' : 'none',
//                                 outlineOffset: 2,
//                             }}
//                         />
//                     ))}
//                 </div>
//             </div>

//             <div className="flex flex-col gap-1 min-w-[60px]">
//                 <label className="text-[10px] text-slate-500 uppercase tracking-wider">Vertices</label>
//                 <span className="text-xs text-slate-300">{polygon.points.length}</span>
//             </div>
//         </div>
//     );
// };

export const Toolbar = ({
    isEditMode,
    activeTool,
    inProgressPoints,
    selectedPolygon,
    polygonCount,
    isSaving,
    canSave,
    saveError,
    openDeletePopover,
    setOpenDeletePopover,
    onEnterEdit,
    onSave,
    onCancelEdit,
    onDeleteSelected,
    onUpload,
    onClearAll,
    onChangePolygonColor,
    onChangePolygonFloorPlan,
}: ToolbarProps) => (
    <div className="flex items-center justify-between gap-4 px-4 py-3 bg-[#1e293b] border-b border-white/10 text-xs text-slate-400 overflow-x-auto">
        <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className="text-slate-300 font-semibold shrink-0">Floor Plan</span>

            {!isEditMode && (
                <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-400 text-[10px] tracking-widest uppercase shrink-0">
                    View only
                </span>
            )}

            {/* <StatusHints
                isEditMode={isEditMode}
                activeTool={activeTool}
                inProgressPoints={inProgressPoints}
                selectedPolygon={selectedPolygon}
                canSave={canSave}
                saveError={saveError}
            /> */}
        </div>

        <div className="flex items-center gap-3 shrink-0">
            {isEditMode && selectedPolygon && (
                <>
                    <Popover
                        placement="bottom"
                        contentClassName="px-6 py-5 rounded-2xl shadow-lg w-[380px]"
                        open={openDeletePopover}
                        setOpen={() => setOpenDeletePopover(true)}
                        content={
                            <div>
                                <p className="text-sm text-grayscale-500">
                                    Are you sure you want to delete this polygon?
                                </p>

                                <div className="flex gap-3 mt-4">
                                    <Button size="xs" color="primary" onClick={onDeleteSelected}>
                                        Delete
                                    </Button>
                                    <Button size="xs" variant="shaded" onClick={() => setOpenDeletePopover(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        }
                    >
                        <Button
                            size="xs"
                            onClick={() => setOpenDeletePopover(true)}
                            disabled={isSaving}
                            className="flex items-center gap-1 px-2 py-1 bg-danger-500 hover:bg-danger-600 rounded transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" />
                            </svg>
                            Delete
                        </Button>
                    </Popover>

                    {/* <InlineProperties
                        polygon={selectedPolygon}
                        onChangeColor={onChangePolygonColor}
                        onChangeFloorPlan={onChangePolygonFloorPlan}
                    /> */}
                </>
            )}

            {!isEditMode ? (
                <div className="flex items-center gap-4">
                    <Button size="xs" variant="default" onClick={onUpload}>
                        Upload
                    </Button>

                    <Button size="xs" onClick={onEnterEdit}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path
                                d="M8.5 1.5l2 2L3 11H1V9L8.5 1.5z"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                fill="none"
                                strokeLinejoin="round"
                            />
                        </svg>
                        Edit
                    </Button>
                </div>
            ) : (
                <>
                    <Button
                        size="xs"
                        variant="outlined"
                        onClick={onCancelEdit}
                        disabled={isSaving}
                        className=" text-slate-400 hover:text-white border border-white/10 hover:border-white/20 rounded transition-all text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </Button>
                    <SaveButton isSaving={isSaving} canSave={canSave} onSave={onSave} />
                </>
            )}
        </div>
    </div>
);
